import { User } from 'models/User';
import { Dispatch, SetStateAction, useCallback } from 'react';
import useSocketEvent from 'services/socketService/hooks/useSocketEvent';
import { MessageCreatedEventData } from 'services/socketService/socketEventData';
import { LISTEN_EVENTS } from 'services/socketService/socketEventNames';

export default function useCurrentUserMessagingCount(
  setCurrentUser: Dispatch<SetStateAction<User>>,
) {
  const markSessionRead = useCallback(
    (sessionId: string) =>
      setCurrentUser((prev) => {
        // Same object keeps every CurrentUser consumer from re-rendering
        if (!prev.unreadSessionIds.includes(sessionId)) return prev;

        return {
          ...prev,
          unreadSessionIds: prev.unreadSessionIds.filter(
            (id) => id !== sessionId,
          ),
        };
      }),
    [setCurrentUser],
  );

  const markSessionUnread = useCallback(
    (sessionId: string) => {
      setCurrentUser((prev) => {
        if (prev.unreadSessionIds.includes(sessionId)) return prev;

        return {
          ...prev,
          unreadSessionIds: [...prev.unreadSessionIds, sessionId],
        };
      });
    },
    [setCurrentUser],
  );

  const onMessageCreated = useCallback(
    ({ message }: MessageCreatedEventData) => {
      markSessionUnread(message.sessionId);
    },
    [markSessionUnread],
  );

  useSocketEvent<MessageCreatedEventData>(
    LISTEN_EVENTS.MESSAGE_CREATED,
    onMessageCreated,
  );

  return {
    markSessionRead,
    markSessionUnread,
  };
}
